import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { useAuth } from '../../contexts/AuthContext';
import { hasAnyRole } from '../../utils/roleUtils';
import './Suppliers.css';

// Deactivating/reactivating a supplier is a MANAGER decision (common/rbac.py)
const STATUS_ROLES = ['ADMIN', 'MANAGER'];

const ViewSupplier = forwardRef(({ supplierId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('procurement');
  const { t: tCommon } = useTranslation('common');
  const [supplier, setSupplier] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [countries, setCountries] = useState([]);

  const { showSuccess, showError, showWarning } = useMessages();
  const { user } = useAuth();
  const canChangeStatus = hasAnyRole(user, STATUS_ROLES);

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (supplierId && !createMode) {
      fetchSupplier();
    } else if (createMode) {
      const newSupplier = {
        provider_code: `SUP-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        provider_name: '',
        contact_person: '',
        email: '',
        phone: '',
        address_line1: '',
        city: '',
        state: '',
        postal_code: '',
        payment_terms: 'NET_30',
        lead_time_days: 15,
        is_active: true
      };
      setSupplier(newSupplier);
      setEditData(newSupplier);
    }
  }, [supplierId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchSupplier = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/providers/${supplierId}`));
      if (response.ok) {
        const data = await response.json();
        setSupplier(data);
        setEditData(data);
      } else {
        setError(t('suppliers.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
      setError(t('suppliers.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/countries'));
      if (response.ok) {
        const data = await response.json();
        setCountries(data.countries || data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!(editData.provider_code || '').trim() || !(editData.provider_name || '').trim()) {
      showWarning(t('suppliers.view.messages.requiredTitle'), t('suppliers.view.messages.requiredFields'));
      return;
    }
    try {
      setError(null);
      const url = createMode
        ? getApiUrl('/api/providers')
        : getApiUrl(`/api/providers/${supplierId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('suppliers.view.messages.createdTitle') : t('suppliers.view.messages.updatedTitle'),
          createMode ? t('suppliers.view.messages.createdSuccess') : t('suppliers.view.messages.updatedSuccess')
        );

        if (createMode && result.provider_id) {
          setSupplier(result);
          setEditData(result);
        } else if (!createMode) {
          await fetchSupplier();
        }

        if (onSave) onSave(result);
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      showError(tCommon('errors.genericTitle'), t('suppliers.view.messages.createFailed'));
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(supplier);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('suppliers.view.messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/providers/${supplierId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('suppliers.view.messages.deletedTitle'), t('suppliers.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      showError(tCommon('errors.genericTitle'), t('suppliers.view.messages.deleteFailed'));
    }
  };

  // Inactive suppliers leave the purchase order supplier list; their history stays (#361)
  const handleToggleActive = async () => {
    const deactivating = supplier.is_active;
    if (deactivating && !window.confirm(t('suppliers.view.messages.deactivateConfirm'))) {
      return;
    }
    try {
      const response = await apiFetch(
        getApiUrl(`/api/providers/${supplierId}/${deactivating ? 'deactivate' : 'activate'}`),
        { method: 'POST' }
      );
      if (response.ok) {
        const result = await response.json();
        setSupplier(result);
        setEditData(result);
        showSuccess(
          t('suppliers.view.messages.updatedTitle'),
          deactivating ? t('suppliers.view.messages.deactivatedSuccess') : t('suppliers.view.messages.activatedSuccess')
        );
        if (onSave) onSave(result);
      } else {
        await showApiError(response, showError, tCommon, t('suppliers.view.messages.updateFailed'));
      }
    } catch (error) {
      console.error('Error changing supplier status:', error);
      showError(tCommon('errors.genericTitle'), t('suppliers.view.messages.updateFailed'));
    }
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('suppliers.view.sections.supplierInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('suppliers.view.fields.code')} *</label>
            <input
              type="text"
              value={editData.provider_code || ''}
              onChange={(e) => handleFieldChange('provider_code', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.name')} *</label>
            <input
              type="text"
              value={editData.provider_name || ''}
              onChange={(e) => handleFieldChange('provider_name', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.paymentTerms')}</label>
            <select
              value={editData.payment_terms || 'NET_30'}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="UPFRONT">{t('suppliers.view.paymentTerms.upfront')}</option>
              <option value="NET_30">{t('suppliers.view.paymentTerms.net30')}</option>
              <option value="NET_45">{t('suppliers.view.paymentTerms.net45')}</option>
              <option value="NET_60">{t('suppliers.view.paymentTerms.net60')}</option>
              <option value="NET_90">{t('suppliers.view.paymentTerms.net90')}</option>
              <option value="NET_120">{t('suppliers.view.paymentTerms.net120')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.leadTime')}</label>
            <input
              type="number"
              value={editData.lead_time_days ?? ''}
              onChange={(e) => handleFieldChange('lead_time_days', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0))}
              disabled={!editMode && !createMode}
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('suppliers.view.sections.contactInfo')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('suppliers.view.fields.contactPerson')}</label>
            <input
              type="text"
              value={editData.contact_person || ''}
              onChange={(e) => handleFieldChange('contact_person', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.contactName')}
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.email')}</label>
            <input
              type="email"
              value={editData.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.email')}
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.phone')}</label>
            <input
              type="text"
              value={editData.phone || ''}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.phone')}
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.taxId')}</label>
            <input
              type="text"
              value={editData.tax_id || ''}
              onChange={(e) => handleFieldChange('tax_id', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.taxId')}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('suppliers.view.sections.addressInfo')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('suppliers.view.fields.address1')}</label>
            <input
              type="text"
              value={editData.address_line1 || ''}
              onChange={(e) => handleFieldChange('address_line1', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.street')}
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.city')}</label>
            <input
              type="text"
              value={editData.city || ''}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.postalCode')}</label>
            <input
              type="text"
              value={editData.postal_code || ''}
              onChange={(e) => handleFieldChange('postal_code', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label htmlFor="supplier-country">{t('suppliers.view.fields.country')}</label>
            <select
              id="supplier-country"
              value={editData.country_id || ''}
              onChange={(e) => handleFieldChange('country_id', e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={!editMode && !createMode}
            >
              <option value="">{t('suppliers.view.placeholders.selectCountry')}</option>
              {countries.map(country => (
                <option key={country.country_id} value={country.country_id}>
                  {country.country_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('suppliers.view.fields.state')}</label>
            <input
              type="text"
              value={editData.state || ''}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.state')}
            />
          </div>
          <div className="form-group full-width">
            <label>{t('suppliers.view.fields.address2')}</label>
            <input
              type="text"
              value={editData.address_line2 || ''}
              onChange={(e) => handleFieldChange('address_line2', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('suppliers.view.placeholders.apt')}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('suppliers.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!supplier && !createMode) {
    return <div className="view-container"><div className="loading">{t('suppliers.view.titles.noSupplierFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('suppliers.view.titles.newSupplier') : editData.provider_name || t('suppliers.view.titles.viewSupplier')}
          </h1>
          {!createMode && editData.provider_code && (
            <span className="type-badge">
              {editData.provider_code}
            </span>
          )}
          {!createMode && supplier && !supplier.is_active && (
            <span className="status-badge status-inactive">{t('suppliers.status.inactive')}</span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('suppliers.toolbar.edit')}</button>
              {canChangeStatus && supplier && (
                <button className={supplier.is_active ? 'btn-danger' : 'btn-primary'} onClick={handleToggleActive}>
                  {supplier.is_active ? t('suppliers.toolbar.deactivate') : t('suppliers.toolbar.activate')}
                </button>
              )}
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('suppliers.toolbar.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('suppliers.toolbar.create') : t('suppliers.toolbar.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('suppliers.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            {t('suppliers.view.tabs.contact')}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'contact' && renderContactTab()}
        </div>
      </div>
    </div>
  );
});

ViewSupplier.displayName = 'ViewSupplier';

export default ViewSupplier;